<script>
import { ref, provide, useTemplateRef } from 'vue';
import { useElementSize } from '@vueuse/core';

import MessageApi from '../../../api/inbox/message';
import { useWebAttendance } from 'dashboard/composables/useWebAttendance';
import { useMapGetter } from 'dashboard/composables/store';
import {
  filterMessagesByDestination,
  supportsWebAttendance,
} from 'dashboard/helper/webAttendance';

// composable
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useLabelSuggestions } from 'dashboard/composables/useLabelSuggestions';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

// components
import ReplyBox from './ReplyBox.vue';
import MessageList from 'next/message/MessageList.vue';
import ConversationLabelSuggestion from './conversation/LabelSuggestion.vue';
import ForwardMessagesModal from './ForwardMessagesModal.vue';
import Banner from 'dashboard/components/ui/Banner.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ResizableEditorWrapper from './ResizableEditorWrapper.vue';

// stores and apis
import { mapGetters } from 'vuex';

// mixins
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';

// utils
import { emitter } from 'shared/helpers/mitt';
import { getTypingUsersText } from '../../../helper/commons';
import { calculateScrollTop } from './helpers/scrollTopCalculationHelper';
import { LocalStorage } from 'shared/helpers/localStorage';
import {
  filterDuplicateSourceMessages,
  getReadMessages,
  getUnreadMessages,
} from 'dashboard/helper/conversationHelper';

// constants
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { REPLY_POLICY } from 'shared/constants/links';
import wootConstants, {
  META_RESTRICTION_STATUS_URL,
} from 'dashboard/constants/globals';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

export default {
  components: {
    Banner,
    Button,
    ConversationLabelSuggestion,
    ForwardMessagesModal,
    MessageList,
    ReplyBox,
    ResizableEditorWrapper,
    Spinner,
  },
  mixins: [inboxMixin],
  setup() {
    const selectedChat = useMapGetter('getSelectedChat');
    const accountId = useMapGetter('getCurrentAccountId');
    const {
      attendance,
      refresh: refreshAttendance,
      seen: markAttendanceSeen,
    } = useWebAttendance(accountId, () => selectedChat.value.id);
    const isPopOutReplyBox = ref(false);
    const conversationPanelRef = ref(null);
    const resizableEditorWrapperRef = ref(null);
    const messagesViewRef = useTemplateRef('messagesViewRef');
    const topBannerRef = useTemplateRef('topBannerRef');
    const { height: containerHeight } = useElementSize(messagesViewRef);
    const { height: topBannerHeight } = useElementSize(topBannerRef);

    const keyboardEvents = {
      Escape: {
        action: () => {
          isPopOutReplyBox.value = false;
        },
      },
    };

    useKeyboardEvents(keyboardEvents);

    const {
      captainTasksEnabled,
      isLabelSuggestionFeatureEnabled,
      getLabelSuggestions,
    } = useLabelSuggestions();

    provide('contextMenuElementTarget', conversationPanelRef);

    return {
      attendance,
      refreshAttendance,
      markAttendanceSeen,
      isPopOutReplyBox,
      captainTasksEnabled,
      getLabelSuggestions,
      isLabelSuggestionFeatureEnabled,
      conversationPanelRef,
      resizableEditorWrapperRef,
      messagesViewRef,
      topBannerRef,
      containerHeight,
      topBannerHeight,
    };
  },
  data() {
    return {
      isLoadingPrevious: true,
      heightBeforeLoad: null,
      conversationPanel: null,
      hasUserScrolled: false,
      isProgrammaticScroll: false,
      messageSentSinceOpened: false,
      labelSuggestions: [],
      forwardSelection: {
        isActive: false,
        selectedMessageIds: [],
      },
      showForwardModal: false,
      highlightedMessageTimer: null,
      deliveryDestination: 'channel',
      attendanceNoteMode: false,
      attendanceError: false,
      attendanceTimer: null,
    };
  },

  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUserId: 'getCurrentUserID',
      listLoadingStatus: 'getAllMessagesLoaded',
      currentAccountId: 'getCurrentAccountId',
      globalConfig: 'globalConfig/get',
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
    }),
    isOpen() {
      return this.currentChat?.status === wootConstants.STATUS_TYPE.OPEN;
    },
    hasWebAttendance() {
      return (
        supportsWebAttendance(this.inbox) &&
        this.inbox.web_attendance_enabled &&
        !this.currentChat.group
      );
    },
    composerDestinations() {
      return this.hasWebAttendance ? ['channel', 'web', 'note'] : ['channel'];
    },
    activeComposer() {
      return this.hasWebAttendance && this.attendanceNoteMode
        ? 'note'
        : this.deliveryDestination;
    },
    attendanceMessageRevision() {
      const messages = this.currentChat.messages || [];
      const last = messages[messages.length - 1];
      return `${this.currentChat.id}:${messages.length}:${last?.id}:${last?.status}`;
    },
    shouldShowLabelSuggestions() {
      return (
        this.isOpen &&
        this.captainTasksEnabled &&
        this.isLabelSuggestionFeatureEnabled &&
        !this.messageSentSinceOpened
      );
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    typingUsersList() {
      const userList = this.$store.getters[
        'conversationTypingStatus/getUserList'
      ](this.currentChat.id);
      return userList;
    },
    isAnyoneTyping() {
      const userList = this.typingUsersList;
      return userList.length !== 0;
    },
    typingUserNames() {
      const userList = this.typingUsersList;
      if (this.isAnyoneTyping) {
        const [i18nKey, params] = getTypingUsersText(userList);
        return this.$t(i18nKey, params);
      }

      return '';
    },
    getMessages() {
      const messages = this.currentChat.messages || [];
      const selectedMessages = this.hasWebAttendance
        ? filterMessagesByDestination(messages, this.deliveryDestination)
        : messages;
      if (this.isAWhatsAppChannel) {
        return filterDuplicateSourceMessages(selectedMessages);
      }
      return selectedMessages;
    },
    readMessages() {
      return getReadMessages(
        this.getMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    unReadMessages() {
      if (this.hasWebAttendance) {
        if (!this.unreadMessageCount) return [];
        return this.getMessages
          .filter(
            message =>
              !message.private &&
              (message.message_type === 0 ||
                message.message_type === 'incoming')
          )
          .slice(-this.unreadMessageCount);
      }
      return getUnreadMessages(
        this.getMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    shouldShowSpinner() {
      return (
        (this.currentChat && this.currentChat.dataFetched === undefined) ||
        (!this.listLoadingStatus && this.isLoadingPrevious)
      );
    },
    // Check there is a instagram inbox exists with the same instagram_id
    hasDuplicateInstagramInbox() {
      const instagramId = this.inbox.instagram_id;
      const { additional_attributes: additionalAttributes = {} } = this.inbox;
      const instagramInbox =
        this.$store.getters['inboxes/getInstagramInboxByInstagramId'](
          instagramId
        );

      return (
        this.inbox.channel_type === INBOX_TYPES.FB &&
        additionalAttributes.type === 'instagram_direct_message' &&
        instagramInbox
      );
    },
    isInstagramRestrictionBannerVisible() {
      return this.isOnChatwootCloud && this.isAnInstagramChannel;
    },
    instagramRestrictionStatusUrl() {
      return META_RESTRICTION_STATUS_URL;
    },

    replyWindowBannerMessage() {
      if (this.isAWhatsAppChannel && !this.isAUnoapiChannel) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_CAN_REPLY');
      }
      if (this.isAPIInbox) {
        const { additional_attributes: additionalAttributes = {} } = this.inbox;
        if (additionalAttributes) {
          const {
            agent_reply_time_window_message: agentReplyTimeWindowMessage,
            agent_reply_time_window: agentReplyTimeWindow,
          } = additionalAttributes;
          return (
            agentReplyTimeWindowMessage ||
            this.$t('CONVERSATION.API_HOURS_WINDOW', {
              hours: agentReplyTimeWindow,
            })
          );
        }
        return '';
      }
      return this.$t('CONVERSATION.CANNOT_REPLY');
    },
    replyWindowLink() {
      if (this.isAFacebookInbox || this.isAnInstagramChannel) {
        return REPLY_POLICY.FACEBOOK;
      }
      if (this.isAWhatsAppCloudChannel) {
        return REPLY_POLICY.WHATSAPP_CLOUD;
      }
      if (this.isATiktokChannel) {
        return REPLY_POLICY.TIKTOK;
      }
      if (!this.isAPIInbox) {
        return REPLY_POLICY.TWILIO_WHATSAPP;
      }
      return '';
    },
    replyWindowLinkText() {
      if (
        (this.isAWhatsAppChannel && !this.isAUnoapiChannel) ||
        this.isAFacebookInbox ||
        this.isAnInstagramChannel
      ) {
        return this.$t('CONVERSATION.24_HOURS_WINDOW');
      }
      if (this.isATiktokChannel) {
        return this.$t('CONVERSATION.48_HOURS_WINDOW');
      }
      if (!this.isAPIInbox) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_24_HOURS_WINDOW');
      }
      return '';
    },
    unreadMessageCount() {
      if (this.hasWebAttendance) {
        return this.attendance?.unread?.[this.deliveryDestination] || 0;
      }
      return this.currentChat.unread_count || 0;
    },
    unreadMessageLabel() {
      const count =
        this.unreadMessageCount > 9 ? '9+' : this.unreadMessageCount;
      const label =
        this.unreadMessageCount > 1
          ? 'CONVERSATION.UNREAD_MESSAGES'
          : 'CONVERSATION.UNREAD_MESSAGE';
      return `${count} ${this.$t(label)}`;
    },
    inboxSupportsReplyTo() {
      const incoming = this.inboxHasFeature(INBOX_FEATURES.REPLY_TO);
      const outgoing =
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO_OUTGOING) &&
        !this.is360DialogWhatsAppChannel;

      return { incoming, outgoing };
    },
    isForwardSelectionActive() {
      return this.forwardSelection.isActive;
    },
    forwardSelectedMessages() {
      if (!this.forwardSelection.isActive) {
        return [];
      }
      const selectedIds = this.forwardSelection.selectedMessageIds;
      if (!selectedIds.length) {
        return [];
      }
      return this.getMessages.filter(message =>
        selectedIds.includes(message.id)
      );
    },
    forwardSelectionCount() {
      return this.forwardSelection.selectedMessageIds.length;
    },
  },

  watch: {
    hasWebAttendance(enabled) {
      if (!enabled) {
        this.deliveryDestination = 'channel';
        this.attendanceNoteMode = false;
      }
      this.loadAttendance();
    },
    attendanceMessageRevision() {
      this.loadAttendance();
    },
    currentChat(newChat, oldChat) {
      if (newChat.id === oldChat.id) {
        return;
      }
      this.fetchAllAttachmentsFromCurrentChat();
      this.fetchSuggestions();
      this.messageSentSinceOpened = false;
      this.deliveryDestination = 'channel';
      this.attendanceNoteMode = false;
      this.loadAttendance();
    },
  },

  created() {
    emitter.on(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
    // when a message is sent we set the flag to true this hides the label suggestions,
    // until the chat is changed and the flag is reset in the watch for currentChat
    emitter.on(BUS_EVENTS.MESSAGE_SENT, () => {
      this.messageSentSinceOpened = true;
    });
    emitter.on(BUS_EVENTS.FORWARD_MESSAGES, this.onForwardMessagesStart);
  },

  mounted() {
    this.addScrollListener();
    this.fetchAllAttachmentsFromCurrentChat();
    this.fetchSuggestions();
    this.loadAttendance();
    this.attendanceTimer = setInterval(() => this.loadAttendance(), 5000);
  },

  unmounted() {
    clearInterval(this.attendanceTimer);
    this.removeBusListeners();
    this.removeScrollListener();
    if (this.highlightedMessageTimer) {
      clearTimeout(this.highlightedMessageTimer);
    }
  },

  methods: {
    async loadAttendance() {
      if (!this.hasWebAttendance) return;
      const id = this.currentChat.id;
      try {
        const data = await this.refreshAttendance();
        if (id !== this.currentChat.id) return;
        this.attendanceError = false;
        const panel = this.conversationPanel;
        const atBottom =
          !panel ||
          panel.scrollHeight - panel.scrollTop - panel.clientHeight < 80;
        if (
          document.visibilityState !== 'hidden' &&
          atBottom &&
          data?.unread?.[this.deliveryDestination]
        ) {
          this.makeMessagesRead();
        }
      } catch {
        if (id === this.currentChat.id) this.attendanceError = true;
      }
    },
    async selectDestination(destination) {
      this.deliveryDestination = destination;
      this.cancelForwardSelection();
      this.isPopOutReplyBox = false;
      this.$nextTick(() => {
        this.scrollToBottom();
        this.makeMessagesRead();
      });
    },
    async fetchSuggestions() {
      // start empty, this ensures that the label suggestions are not shown
      this.labelSuggestions = [];

      if (this.isLabelSuggestionDismissed()) {
        return;
      }

      // Early exit if conversation already has labels - no need to suggest more
      const existingLabels = this.currentChat?.labels || [];
      if (existingLabels.length > 0) return;

      if (!this.captainTasksEnabled || !this.isLabelSuggestionFeatureEnabled) {
        return;
      }

      this.labelSuggestions = await this.getLabelSuggestions();

      // once the labels are fetched, we need to scroll to bottom
      // but we need to wait for the DOM to be updated
      // so we use the nextTick method
      this.$nextTick(() => {
        // this param is added to route, telling the UI to navigate to the message
        // it is triggered by the SCROLL_TO_MESSAGE method
        // see setActiveChat on ConversationView.vue for more info
        const { messageId } = this.$route.query;

        // only trigger the scroll to bottom if the user has not scrolled
        // and there's no active messageId that is selected in view
        if (!messageId && !this.hasUserScrolled) {
          this.scrollToBottom();
        }
      });
    },
    isLabelSuggestionDismissed() {
      return LocalStorage.getFlag(
        LOCAL_STORAGE_KEYS.DISMISSED_LABEL_SUGGESTIONS,
        this.currentAccountId,
        this.currentChat.id
      );
    },
    fetchAllAttachmentsFromCurrentChat() {
      this.$store.dispatch('fetchAllAttachments', this.currentChat.id);
    },
    removeBusListeners() {
      emitter.off(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
      emitter.off(BUS_EVENTS.FORWARD_MESSAGES, this.onForwardMessagesStart);
    },
    highlightMessageElement(messageElement) {
      if (this.highlightedMessageTimer) {
        clearTimeout(this.highlightedMessageTimer);
        this.highlightedMessageTimer = null;
      }

      const previousHighlighted = this.conversationPanel.querySelector(
        '[data-quoted-highlight="true"]'
      );
      if (previousHighlighted && previousHighlighted !== messageElement) {
        previousHighlighted.removeAttribute('data-quoted-highlight');
        previousHighlighted.style.boxShadow = '';
        previousHighlighted.style.borderRadius = '';
        previousHighlighted.style.transition = '';
      }

      messageElement.setAttribute('data-quoted-highlight', 'true');
      messageElement.style.transition = 'box-shadow 150ms ease';
      messageElement.style.borderRadius = '0.75rem';
      messageElement.style.boxShadow =
        '0 0 0 2px rgb(59 130 246), 0 0 0 6px rgb(59 130 246 / 0.18)';

      this.highlightedMessageTimer = setTimeout(() => {
        messageElement.removeAttribute('data-quoted-highlight');
        messageElement.style.boxShadow = '';
        messageElement.style.borderRadius = '';
        messageElement.style.transition = '';
        this.highlightedMessageTimer = null;
      }, 2500);
    },
    onScrollToMessage({ messageId = '' } = {}) {
      this.$nextTick(() => {
        const messageElement = document.getElementById('message' + messageId);
        if (messageElement) {
          this.isProgrammaticScroll = true;
          messageElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          this.highlightMessageElement(messageElement);
        } else {
          this.scrollToBottom();
        }
      });
      this.makeMessagesRead();
    },
    onForwardMessagesStart({ messageId } = {}) {
      if (!messageId) {
        return;
      }
      this.forwardSelection.isActive = true;
      this.forwardSelection.selectedMessageIds = [messageId];
    },
    toggleForwardSelection(messageId) {
      if (!this.forwardSelection.isActive) {
        return;
      }
      const selectedIds = this.forwardSelection.selectedMessageIds;
      const index = selectedIds.indexOf(messageId);
      if (index === -1) {
        selectedIds.push(messageId);
      } else {
        selectedIds.splice(index, 1);
      }
      if (!selectedIds.length) {
        this.cancelForwardSelection();
      }
    },
    cancelForwardSelection() {
      this.forwardSelection.isActive = false;
      this.forwardSelection.selectedMessageIds = [];
      this.showForwardModal = false;
    },
    openForwardModal() {
      if (!this.forwardSelection.selectedMessageIds.length) {
        return;
      }
      this.showForwardModal = true;
    },
    onForwardCompleted(conversation) {
      this.cancelForwardSelection();
      if (!conversation || !conversation.id) {
        return;
      }
      this.$router.push({
        name: 'inbox_conversation',
        params: {
          accountId: this.currentAccountId,
          conversation_id: conversation.id,
        },
      });
    },
    addScrollListener() {
      this.conversationPanel = this.$el.querySelector('.conversation-panel');
      this.setScrollParams();
      this.conversationPanel.addEventListener('scroll', this.handleScroll);
      this.$nextTick(() => this.scrollToBottom());
      this.isLoadingPrevious = false;
    },
    removeScrollListener() {
      this.conversationPanel.removeEventListener('scroll', this.handleScroll);
    },
    scrollToBottom() {
      this.isProgrammaticScroll = true;
      let relevantMessages = [];

      // label suggestions are not part of the messages list
      // so we need to handle them separately
      let labelSuggestions =
        this.conversationPanel.querySelector('.label-suggestion');

      // if there are unread messages, scroll to the first unread message
      if (this.unreadMessageCount > 0) {
        // capturing only the unread messages
        relevantMessages =
          this.conversationPanel.querySelectorAll('.message--unread');
      } else if (labelSuggestions) {
        // when scrolling to the bottom, the label suggestions is below the last message
        // so we scroll there if there are no unread messages
        // Unread messages always take the highest priority
        relevantMessages = [labelSuggestions];
      } else {
        // if there are no unread messages or label suggestion, scroll to the last message
        // capturing last message from the messages list
        relevantMessages = Array.from(
          this.conversationPanel.querySelectorAll('.message--read')
        ).slice(-1);
      }

      this.conversationPanel.scrollTop = calculateScrollTop(
        this.conversationPanel.scrollHeight,
        this.$el.scrollHeight,
        relevantMessages
      );
    },
    setScrollParams() {
      this.heightBeforeLoad = this.conversationPanel.scrollHeight;
      this.scrollTopBeforeLoad = this.conversationPanel.scrollTop;
    },

    async fetchPreviousMessages(scrollTop = 0) {
      this.setScrollParams();
      const shouldLoadMoreMessages =
        this.currentChat.dataFetched === true &&
        !this.listLoadingStatus &&
        !this.isLoadingPrevious;

      if (
        scrollTop < 100 &&
        !this.isLoadingPrevious &&
        shouldLoadMoreMessages
      ) {
        this.isLoadingPrevious = true;
        try {
          await this.$store.dispatch('fetchPreviousMessages', {
            conversationId: this.currentChat.id,
            before: this.currentChat.messages[0].id,
          });
          const heightDifference =
            this.conversationPanel.scrollHeight - this.heightBeforeLoad;
          this.conversationPanel.scrollTop =
            this.scrollTopBeforeLoad + heightDifference;
          this.setScrollParams();
        } catch (error) {
          // Ignore Error
        } finally {
          this.isLoadingPrevious = false;
        }
      }
    },

    handleScroll(e) {
      if (this.isProgrammaticScroll) {
        // Reset the flag
        this.isProgrammaticScroll = false;
        this.hasUserScrolled = false;
      } else {
        this.hasUserScrolled = true;
      }
      emitter.emit(BUS_EVENTS.ON_MESSAGE_LIST_SCROLL);
      this.fetchPreviousMessages(e.target.scrollTop);
    },

    makeMessagesRead() {
      if (this.hasWebAttendance) {
        this.markAttendanceSeen(this.deliveryDestination).catch(() => {
          this.attendanceError = true;
        });
        return;
      }
      this.$store.dispatch('markMessagesRead', { id: this.currentChat.id });
    },
    async handleMessageRetry(message) {
      if (!message) return;
      const payload = useSnakeCase(message);
      await this.$store.dispatch('sendMessageWithData', payload);
    },

    async getInReplyToMessage(parentMessage) {
      if (!parentMessage) return {};
      const inReplyToMessageId = parentMessage.content_attributes?.in_reply_to;
      if (!inReplyToMessageId) return {};
      let replyToMessage = this.currentChat?.messages.find(message => {
        if (message.id === inReplyToMessageId) {
          return true;
        }
        return false;
      });
      if (!replyToMessage) {
        const params = {
          conversationId: this.currentChat.id,
          after: inReplyToMessageId - 1,
          before: inReplyToMessageId + 1,
        };
        const {
          data: { payload },
        } = await MessageApi.getPreviousMessages(params);
        replyToMessage = payload[0];
      }
      return replyToMessage;
    },
    toggleReplyEditorSize() {
      this.resizableEditorWrapperRef?.toggleEditorExpand?.();
    },
    resetReplyEditorHeight() {
      this.resizableEditorWrapperRef?.resetEditorHeight?.();
    },
  },
};
</script>

<template>
  <div
    ref="messagesViewRef"
    class="flex flex-col justify-between flex-grow h-full min-w-0 m-0"
  >
    <div ref="topBannerRef">
      <div
        v-if="hasWebAttendance"
        class="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-n-weak"
        role="tablist"
      >
        <button
          v-for="destination in ['channel', 'web']"
          :key="destination"
          type="button"
          role="tab"
          :aria-selected="deliveryDestination === destination"
          class="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm min-w-0"
          :class="
            deliveryDestination === destination
              ? 'bg-n-alpha-2 text-n-brand'
              : 'text-n-slate-11'
          "
          @click="selectDestination(destination)"
        >
          {{
            destination === 'web'
              ? $t('WEB_ATTENDANCE.WEB_TAB')
              : $t('WEB_ATTENDANCE.CHANNEL_TAB')
          }}
          <span
            v-if="attendance?.unread?.[destination]"
            class="rounded-full bg-n-brand px-2 text-white text-xs"
          >
            {{ attendance.unread[destination] }}
          </span>
        </button>
      </div>
      <div
        v-if="hasWebAttendance && attendanceError"
        class="px-3 py-2 text-sm text-n-amber-11"
        role="status"
      >
        {{ $t('WEB_ATTENDANCE.LOAD_ERROR') }}
        <button
          type="button"
          class="text-n-brand underline"
          @click="loadAttendance"
        >
          {{ $t('WEB_ATTENDANCE.RETRY') }}
        </button>
      </div>
      <Banner
        v-if="
          hasWebAttendance &&
          deliveryDestination === 'web' &&
          !attendance?.active
        "
        color-scheme="warning"
        class="mx-2 mt-2 rounded-lg"
        :banner-message="$t('WEB_ATTENDANCE.INACTIVE')"
      />
      <Banner
        v-if="isInstagramRestrictionBannerVisible"
        color-scheme="warning"
        class="mx-2 mt-2 overflow-hidden rounded-lg"
        :banner-message="$t('CONVERSATION.INSTAGRAM_RESTRICTION_BANNER')"
        :href-link="instagramRestrictionStatusUrl"
        :href-link-text="$t('CONVERSATION.INSTAGRAM_RESTRICTION_STATUS_LINK')"
      />
      <Banner
        v-else-if="!currentChat.can_reply && deliveryDestination !== 'web'"
        color-scheme="alert"
        class="mx-2 mt-2 overflow-hidden rounded-lg"
        :banner-message="replyWindowBannerMessage"
        :href-link="replyWindowLink"
        :href-link-text="replyWindowLinkText"
      />
      <Banner
        v-else-if="hasDuplicateInstagramInbox"
        color-scheme="alert"
        class="mx-2 mt-2 overflow-hidden rounded-lg"
        :banner-message="$t('CONVERSATION.OLD_INSTAGRAM_INBOX_REPLY_BANNER')"
      />
    </div>
    <ForwardMessagesModal
      v-if="isForwardSelectionActive"
      v-model:show="showForwardModal"
      :selected-messages="forwardSelectedMessages"
      :conversation-id="currentChat.id"
      @forwarded="onForwardCompleted"
      @close="cancelForwardSelection"
    />
    <div
      v-if="isForwardSelectionActive"
      class="flex items-center justify-between mx-2 mt-2 mb-1 rounded-lg bg-n-alpha-2 px-3 py-2"
    >
      <p class="m-0 text-xs font-medium text-n-slate-12">
        {{
          $t('CONVERSATION.FORWARD_MESSAGES.SELECTED_COUNT', {
            count: forwardSelectionCount,
          })
        }}
      </p>
      <div class="flex items-center gap-2">
        <Button
          variant="ghost"
          color="slate"
          size="xs"
          :label="$t('CONVERSATION.FORWARD_MESSAGES.CANCEL')"
          @click="cancelForwardSelection"
        />
        <Button
          color="blue"
          size="xs"
          :disabled="forwardSelectionCount === 0"
          :label="$t('CONVERSATION.FORWARD_MESSAGES.ACTION_LABEL')"
          @click="openForwardModal"
        />
      </div>
    </div>
    <MessageList
      ref="conversationPanelRef"
      class="conversation-panel flex-shrink flex-grow basis-px flex flex-col overflow-y-auto relative h-full m-0 pb-4"
      :current-user-id="currentUserId"
      :first-unread-id="unReadMessages[0]?.id"
      :is-an-email-channel="isAnEmailChannel"
      :inbox-supports-reply-to="inboxSupportsReplyTo"
      :style="globalConfig.conversationStyleCss"
      :messages="getMessages"
      :is-forward-selection-active="isForwardSelectionActive"
      :forward-selected-message-ids="forwardSelection.selectedMessageIds"
      @retry="handleMessageRetry"
      @toggle-forward-selection="toggleForwardSelection"
    >
      <template #beforeAll>
        <transition name="slide-up">
          <!-- eslint-disable-next-line vue/require-toggle-inside-transition -->
          <li
            class="min-h-[4rem] flex flex-shrink-0 flex-grow-0 items-center flex-auto justify-center max-w-full mt-0 mr-0 mb-1 ml-0 relative first:mt-auto last:mb-0"
          >
            <Spinner v-if="shouldShowSpinner" class="text-n-brand" />
          </li>
        </transition>
      </template>
      <template #unreadBadge>
        <li
          v-show="unreadMessageCount != 0"
          class="list-none flex justify-center items-center"
        >
          <span
            class="shadow-lg rounded-full bg-n-brand text-white text-xs font-medium my-2.5 mx-auto px-2.5 py-1.5"
          >
            {{ unreadMessageLabel }}
          </span>
        </li>
      </template>
      <template #after>
        <ConversationLabelSuggestion
          v-if="shouldShowLabelSuggestions"
          :suggested-labels="labelSuggestions"
          :chat-labels="currentChat.labels"
          :conversation-id="currentChat.id"
        />
      </template>
    </MessageList>
    <div
      class="flex relative flex-col"
      :class="{
        'modal-mask': isPopOutReplyBox,
        'bg-n-surface-1': !isPopOutReplyBox,
      }"
    >
      <div
        v-if="isAnyoneTyping"
        class="absolute flex items-center w-full h-0 -top-7"
      >
        <div
          class="flex py-2 pr-4 pl-5 shadow-md rounded-full bg-white dark:bg-n-solid-3 text-n-slate-11 text-xs font-semibold my-2.5 mx-auto"
        >
          {{ typingUserNames }}
          <img
            class="w-6 ltr:ml-2 rtl:mr-2"
            src="assets/images/typing.gif"
            alt="Someone is typing"
          />
        </div>
      </div>
      <ResizableEditorWrapper
        ref="resizableEditorWrapperRef"
        :container-height="Math.max(0, containerHeight - topBannerHeight)"
      >
        <div
          v-for="destination in composerDestinations"
          v-show="activeComposer === destination"
          :key="destination"
          class="min-w-0"
        >
          <ReplyBox
            :delivery-destination="destination === 'web' ? 'web' : 'channel'"
            :is-composer-active="activeComposer === destination"
            :managed-destinations="hasWebAttendance"
            :initial-reply-mode="destination === 'note' ? 'NOTE' : 'REPLY'"
            :web-attendance-active="
              !attendanceError && Boolean(attendance?.active)
            "
            :pop-out-reply-box="isPopOutReplyBox"
            @set-reply-mode="attendanceNoteMode = $event === 'NOTE'"
            @toggle-editor-size="toggleReplyEditorSize"
            @update:pop-out-reply-box="isPopOutReplyBox = $event"
          />
        </div>
      </ResizableEditorWrapper>
    </div>
  </div>
</template>

<style scoped lang="scss">
.modal-mask {
  @apply fixed;

  &::v-deep {
    .ProseMirror-woot-style {
      @apply max-h-[25rem];
    }

    .reply-box {
      @apply border border-n-weak max-w-[75rem] w-[70%];

      &.is-private {
        @apply dark:border-n-amber-3/30 border-n-amber-12/5;
      }
    }

    .reply-box .reply-box__top {
      @apply relative min-h-[27.5rem];
    }

    .reply-box__top .input {
      @apply min-h-[27.5rem];
    }

    .emoji-dialog {
      @apply absolute ltr:left-auto rtl:right-auto bottom-1;
    }
  }
}
</style>
